import React, { useState } from 'react';
import { Share2, Trash2, ChevronDown, ChevronUp, FileText, Clock, HardDrive, FileDown, Image, X, Globe, Lock } from 'lucide-react';
import VideoDetailsModal from './VideoDetailsModal';
import TranscriptModal from './TranscriptModal';
import ShareModal from './ShareModal';
import { deleteVideo, setVideoPublic } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import VideoReaction from './VideoReaction';
import './ModernVideoCard.css';

export default function ModernVideoCard({ video, onDelete }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [showDetails, setShowDetails] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showQuestionImage, setShowQuestionImage] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPublic, setIsPublic] = useState(video.is_public || false);
  const [togglingPublic, setTogglingPublic] = useState(false);

  const handlePlay = () => {
    if (video.status === 'completed' && video.video_url) {
      setIsPlaying(true);
    }
  };

  const handleShare = () => {
    setShowShare(true);
  };

  const handleCloseShare = () => {
    setShowShare(false);
  };
  
  const handleCloseTranscript = () => {
    setShowTranscript(false);
  };
  
  const handleTranscript = () => {
    setShowTranscript(true);
  };

  const handleQuestionImage = () => {
    setShowQuestionImage(true);
  };

  const handleCloseQuestionImage = () => {
    setShowQuestionImage(false);
  };

  const handleDelete = async () => {
    const confirmMessage = t.videoCard?.confirmDelete || 'Are you sure you want to delete this video?';
    if (window.confirm(confirmMessage)) {
      try {
        await deleteVideo(video.id);
        if (onDelete) {
          onDelete(video.id);
        }
      } catch (error) {
        console.error('Error deleting video:', error);
        alert(t.videoCard?.deleteError || 'Failed to delete video');
      }
    }
  };

  const handleTogglePublic = async () => {
    if (togglingPublic) return;
    setTogglingPublic(true);
    const next = !isPublic;
    setIsPublic(next); // optimistic
    try {
      await setVideoPublic(video.id, next);
    } catch (_) {
      setIsPublic(!next); // rollback
    } finally {
      setTogglingPublic(false);
    }
  };

  const getLanguageFlag = (lang) => {
    switch (lang) {
      case 'fi': return '🇫🇮';
      case 'en': return '🇬🇧';
      case 'sv': return '🇸🇪';
      default: return '🌐';
    }
  };

  const getQualityDisplay = (quality) => {
    switch (quality) {
      case 'qk': return '4K';
      case 'qh': return '1080p';
      case 'qm': return '720p';
      case 'ql': return '480p';
      default: return quality;
    }
  };

  const getSubjectEmoji = (subject) => {
    switch (subject?.toLowerCase()) {
      case 'chemistry': return '🧪';
      case 'physics': return '⚛️';
      case 'mathematics': return '📐';
      default: return '📚';
    }
  };

  const getSubjectName = (subject) => {
    if (!subject) return t.videoCard?.noSubject || 'General';
    
    const subjectMap = {
      'chemistry': t.chemistry || 'Chemistry',
      'physics': t.physics || 'Physics',
      'mathematics': t.mathematics || 'Mathematics',
    };
    
    return subjectMap[subject.toLowerCase()] || subject;
  };
  
  const getStatusBadge = (status) => {
    const statusMap = {
      'completed': { 
        text: t.videoDetails?.completed || 'Ready', 
        color: '#10b981', 
        bg: '#d1fae5' 
      },
      'rendering': { 
        text: t.videoDetails?.processing || 'Rendering', 
        color: '#3b82f6', 
        bg: '#dbeafe' 
      },
      'queued': { 
        text: t.videoDetails?.pending || 'Queued', 
        color: '#f59e0b', 
        bg: '#fef3c7' 
      },
      'failed': { 
        text: t.videoDetails?.failed || 'Failed', 
        color: '#ef4444', 
        bg: '#fee2e2' 
      },
    };
    
    return statusMap[status] || { 
      text: 'Unknown', 
      color: '#6b7280', 
      bg: '#f3f4f6' 
    };
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return t.videoCard?.unknown || 'Unknown';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return t.videoCard?.unknown || 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const status = getStatusBadge(video.status);
  const isPlayable = video.status === 'completed' && video.video_url;

  return (
    <>
      <div className="modern-video-card">
        {/* Thumbnail or Video Player */}
        <div className="card-media">
          {!isPlaying || !video.video_url ? (
            <div 
              className="card-thumbnail"
              onClick={handlePlay}
              style={{ cursor: isPlayable ? 'pointer' : 'default' }}
            >
              {video.thumbnail_url ? (
                <img src={video.thumbnail_url} alt={video.scene_name} />
              ) : (
                <div className="thumbnail-placeholder">
                  <div className="placeholder-icon">🎬</div>
                </div>
              )}
              
              {/* Status Badge - Top Left */}
              <div
                className="status-badge-overlay"
                style={{ backgroundColor: status.bg, color: status.color }}
              >
                {status.text}
              </div>

              {/* Public Badge - Top Right */}
              {isPublic && (
                <div className="public-badge-overlay">
                  <Globe size={11} /> {t.videoCard?.public || 'Public'}
                </div>
              )}

              {/* Play Overlay - Only for completed videos */}
              {isPlayable && (
                <div className="play-overlay" onClick={handlePlay}>
                  <div className="play-button">
                    <div className="play-icon">▶</div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="video-player-wrapper">
              <video
                key={video.id}
                controls
                controlsList="nodownload"
                autoPlay
                className="video-player"
                onEnded={() => setIsPlaying(false)}
              >
                <source src={video.video_url} type="video/mp4" />
                {t.videoCard?.noVideoSupport || 'Your browser does not support the video tag.'}
              </video>
            </div>
          )}
        </div>

        {/* Reaction bar — directly under video */}
        {video.status === 'completed' && <VideoReaction videoId={video.id} showCounts compact />}

        {/* Video Info */}
        <div className="card-content">
          <h3 className="card-title">{video.scene_name || t.videoCard?.untitled || 'Untitled Video'}</h3>
          
          {/* Meta Badges */}
          <div className="card-meta">
            <span className="meta-badge language" title={t.videoCard?.language || 'Language'}>
              {getLanguageFlag(video.language)}
            </span>
            <span className="meta-badge quality" title={t.videoCard?.quality || 'Quality'}>
              {getQualityDisplay(video.quality)}
            </span>
            <span className="meta-badge subject" title={t.videoCard?.subject || 'Subject'}>
              {getSubjectEmoji(video.subject)} {getSubjectName(video.subject)}
            </span>
            <span className="meta-badge date" title={t.videoCard?.created || 'Created'}>
              {new Date(video.created_at).toLocaleDateString()}
            </span>
          </div>

          {/* Actions */}
          <div className="card-actions">
            {isPlayable && (
              <button
                onClick={handleShare}
                className="action-btn share"
                title={t.videoCard?.share || 'Share video'}
              >
                <Share2 size={24} strokeWidth={2} />
              </button>
            )}

            {/* Public / Private toggle — only for completed videos */}
            {isPlayable && (
              <button
                onClick={handleTogglePublic}
                className={`action-btn visibility${isPublic ? ' visibility--public' : ''}`}
                title={isPublic ? (t.videoCard?.makePrivate || 'Make private') : (t.videoCard?.makePublic || 'Make public')}
                disabled={togglingPublic}
              >
                {isPublic ? <Globe size={24} strokeWidth={2} /> : <Lock size={24} strokeWidth={2} />}
              </button>
            )}
            
            {/* Only show transcript button if transcript exists */}
            {video.transcript_markdown && (
              <button 
                onClick={handleTranscript} 
                className="action-btn transcript"
                title={t.videoCard?.transcript || 'Download transcript'}
              >
                <FileDown size={24} strokeWidth={2} />
              </button>
            )}

            {/* Question Image Button */}
            {video.question_image_url && (
              <button
                onClick={handleQuestionImage}
                className="action-btn question-image"
                title={t.videoCard?.viewQuestionImage || 'View question image'}
              >
                <Image size={24} strokeWidth={2} />
              </button>
            )}

            <button 
              onClick={handleDelete} 
              className="action-btn delete"
              title={t.videoCard?.delete || 'Delete video'}
            >
              <Trash2 size={24} strokeWidth={2} />
            </button>
            <button 
              onClick={() => setShowInfo(!showInfo)} 
              className="action-btn info-toggle"
              title={showInfo ? (t.videoCard?.hideInfo || 'Hide info') : (t.videoCard?.showInfo || 'Show info')}
            >
              {showInfo ? <ChevronUp size={24} strokeWidth={2} /> : <ChevronDown size={24} strokeWidth={2} />}
            </button>
          </div>

          {/* Collapsible Info Section */}
          {showInfo && (
            <div className="card-info-expanded">
              <div className="info-row">
                <div className="info-item">
                  <HardDrive size={16} className="info-icon" />
                  <span className="info-label">{t.videoCard?.fileSize || 'Size'}:</span>
                  <span className="info-value">{formatFileSize(video.file_size)}</span>
                </div>
                <div className="info-item">
                  <Clock size={16} className="info-icon" />
                  <span className="info-label">{t.videoCard?.duration || 'Duration'}:</span>
                  <span className="info-value">{formatDuration(video.duration)}</span>
                </div>
              </div>
              
              {video.prompt && (
                <div className="info-prompt">
                  <div className="info-prompt-header">
                    <FileText size={16} className="info-icon" />
                    <span className="info-label">{t.videoCard?.prompt || 'Prompt'}:</span>
                  </div>
                  <p className="info-prompt-text">{video.prompt}</p>
                </div>
              )}

              <button 
                onClick={() => setShowDetails(true)} 
                className="btn-full-details"
              >
                {t.videoCard?.viewFullDetails || 'View Full Details'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetails && (
        <VideoDetailsModal 
          videoId={video.id} 
          onClose={() => setShowDetails(false)} 
        />
      )}

      {/* Share Modal */}
      {showShare && (
        <ShareModal
          isOpen={showShare}
          onClose={handleCloseShare}
          videoId={video.id}
          sceneName={video.scene_name}
        />
      )}
      
      {/* Transcript Modal */}
      {showTranscript && video.transcript_markdown && (
        <TranscriptModal
          open={showTranscript}
          onClose={handleCloseTranscript}
          transcript={video.transcript_markdown}
        />
      )}

      {/* Question Image Modal */}
      {showQuestionImage && video.question_image_url && (
        <div className="question-image-modal-overlay" onClick={handleCloseQuestionImage}>
          <div className="question-image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="question-image-modal-header">
              <h3 className="question-image-modal-title">{t.videoCard?.questionImage || 'Question Image'}</h3>
              <button 
                className="question-image-modal-close" 
                onClick={handleCloseQuestionImage}
              >
                <X />
              </button>
            </div>
            <div className="question-image-modal-body">
              <img src={video.question_image_url} alt="Question" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}